import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

/** "May 27, 2026" */
export function formatDate(date: string | Date): string {
	return new Date(date).toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	});
}

/**
 * Calendar dates — 'YYYY-MM-DD' with no time of day, like a birthday — must not
 * shift with the viewer's timezone. Both of the obvious conversions do shift it:
 * `new Date('1990-05-14')` parses as UTC midnight (a day earlier west of UTC),
 * and `toISOString()` converts back through UTC (a day earlier east of it, so
 * picking a date in Copenhagen and saving it loses a day). These helpers stay in
 * local date components end to end and never touch UTC.
 */
export function parseCalendarDate(value?: string | null): Date | null {
	if (!value) return null;
	const [year, month, day] = value.split('-').map(Number);
	if (!year || !month || !day) return null;
	return new Date(year, month - 1, day);
}

/** Inverse of parseCalendarDate: a local Date back to 'YYYY-MM-DD'. */
export function toCalendarDate(date?: Date | null): string | null {
	if (!date) return null;
	const pad = (n: number) => String(n).padStart(2, '0');
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** "May 14, 1990" for a 'YYYY-MM-DD' calendar date. Null when unparseable. */
export function formatCalendarDate(value?: string | null): string | null {
	const date = parseCalendarDate(value);
	if (!date) return null;
	return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** "2h ago", "3d ago", or falls back to formatDate */
export function timeAgo(date: string | Date): string {
	const now = Date.now();
	const then = new Date(date).getTime();
	const diff = Math.max(0, now - then);
	const minutes = Math.floor(diff / 60000);
	if (minutes < 1) return 'Just now';
	if (minutes < 60) return `${minutes}m ago`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours}h ago`;
	const days = Math.floor(hours / 24);
	if (days < 7) return `${days}d ago`;
	return formatDate(date);
}

/** "May 2026" */
export function formatMonthYear(date: string | Date): string {
	return new Date(date).toLocaleDateString('en-US', {
		month: 'short',
		year: 'numeric',
	});
}

/**
 * Formats a projected-delivery window ("Jun 3 – Jun 18"). Collapses to a single
 * date when only one bound is present or both fall on the same day. Returns
 * `null` when neither bound is set (e.g. production time isn't known yet), so
 * callers can hide the field.
 */
export function formatArrivalWindow(
	earliest?: string | null,
	latest?: string | null
): string | null {
	const short = (date: string) =>
		new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
	if (earliest && latest) {
		const a = short(earliest);
		const b = short(latest);
		return a === b ? a : `${a} – ${b}`;
	}
	if (earliest) return short(earliest);
	if (latest) return short(latest);
	return null;
}
